import React, { useEffect, useMemo, useState } from 'react';
import { authErrorMessage } from '../utils/errorMessages';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import { APP_CONFIG } from '../config/AppConfig';
import { useAuthContext } from '../contexts/AuthContext';

const normalizeReturnTo = value => {
  if (!value || !value.startsWith('/')) {
    return '/';
  }
  return value;
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, isLoading } = useAuthContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resolvingPostLoginRoute, setResolvingPostLoginRoute] = useState(false);
  const [error, setError] = useState('');

  const returnTo = useMemo(() => normalizeReturnTo(searchParams.get('returnTo')), [searchParams]);

  useEffect(() => {
    if (isAuthenticated && !isLoading && !resolvingPostLoginRoute) {
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, returnTo, resolvingPostLoginRoute]);

  const handleSubmit = async event => {
    event.preventDefault();
    setSubmitting(true);
    setResolvingPostLoginRoute(true);
    setError('');

    try {
      await login(email.trim(), password);
      // Back to where sign-in was asked for; entitlement is decided at play, not here.
      navigate(returnTo, { replace: true });
    } catch (loginError) {
      setError(authErrorMessage(loginError, 'Unable to sign in. Please try again.'));
    } finally {
      setSubmitting(false);
      setResolvingPostLoginRoute(false);
    }
  };

  return (
    <AuthLayout title="Sign In" subtitle="Log in with your BKFC account to continue watching.">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="login-email" className="block text-sm text-text-muted mb-2">
            Email
          </label>
          <input
            id="login-email"
            type="email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            autoComplete="email"
            required
            className="w-full rounded-lg bg-secondary border border-gray-border px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent"
          />
        </div>

        <div>
          <label htmlFor="login-password" className="block text-sm text-text-muted mb-2">
            Password
          </label>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={event => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              className="w-full rounded-lg bg-secondary border border-gray-border px-4 py-3 pr-12 text-white focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent"
            />
            <button
              type="button"
              onClick={() => setShowPassword(prev => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="absolute inset-y-0 right-0 flex items-center pr-4 text-accent hover:text-accent-hover focus:outline-none focus-visible:text-accent-hover transition-colors"
            >
              {showPassword ? (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.243 4.243L9.88 9.88"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-400 bg-red-950/40 border border-red-900 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-accent text-primary hover:bg-accent-hover disabled:opacity-60 disabled:cursor-not-allowed px-4 py-3 font-semibold transition-colors"
        >
          {submitting ? 'Signing in...' : 'Sign In'}
        </button>
      </form>

      <div className="mt-6 flex items-center justify-between text-sm">
        <Link
          to={`/forgot-password?email=${encodeURIComponent(email.trim())}`}
          className="text-text-muted hover:text-white"
        >
          Forgot password?
        </Link>
        <Link
          to={`/signup?returnTo=${encodeURIComponent(returnTo)}`}
          className="text-accent hover:text-accent-hover"
        >
          Create account
        </Link>
      </div>

      <p className="mt-4 text-center text-sm text-text-muted">
        Need help?{' '}
        <a
          href={APP_CONFIG.SUPPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent hover:text-accent-hover"
        >
          Contact Support
        </a>
      </p>
    </AuthLayout>
  );
};

export default LoginPage;
